import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Language toggle for NO-LOGIN public surfaces (vendor/freelancer secure-link
 * pages, crew links, CSAT, visit-ack) — multi-language increment 3.
 *
 * These pages have no /me (no user, no tenant flag), so the choice lives in
 * the viewer's browser (localStorage) and defaults to English. It only
 * affects the viewer's own device; the tenant `multi_language` flag governs
 * the logged-in app, while an external engineer/customer picking their own
 * language here is always safe (English fallback per natural keys).
 *
 * Pages re-render via usePublicLanguage(): key your page root with the
 * returned code so a switch remounts the content (same pattern as the
 * AppShell Outlet key).
 */
import { Languages } from 'lucide-react';
import { useEffect, useSyncExternalStore } from 'react';
import i18n, { applyLocale } from '@/lib/i18n';
import { LANGUAGES, isSupportedLanguage } from '@/lib/languages';
const STORE_KEY = 'styx-public-lang';
function subscribe(cb) {
    i18n.on('languageChanged', cb);
    return () => i18n.off('languageChanged', cb);
}
/** Current interface language; subscribes so a switch re-renders the caller. */
export function usePublicLanguage() {
    // Apply the remembered choice on first mount (deep links, reloads).
    useEffect(() => {
        const saved = localStorage.getItem(STORE_KEY);
        if (isSupportedLanguage(saved) && saved !== i18n.language)
            void applyLocale(saved);
    }, []);
    return useSyncExternalStore(subscribe, () => i18n.language);
}
export function PublicLanguageToggle({ className }) {
    const lang = usePublicLanguage();
    return (_jsxs("div", { className: `flex items-center gap-1.5 ${className ?? ''}`, children: [_jsx(Languages, { size: 14, className: "shrink-0 text-slate-400" }), LANGUAGES.map((l) => (_jsx("button", { type: "button", onClick: () => {
                    localStorage.setItem(STORE_KEY, l.code);
                    void applyLocale(l.code);
                }, className: lang === l.code
                    ? 'rounded-md bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-800 dark:bg-slate-700 dark:text-slate-100'
                    : 'rounded-md px-2 py-0.5 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800', children: l.nativeName }, l.code)))] }));
}
